import { useEffect } from 'react';
import { useQueryClient } from 'react-query';

import { Company, DataPeriod } from 'interfaces/global/globalInterfaces';

import {
  buildProgramsQueryParams,
  fetchProgramsPage,
  getProgramsQueryKey,
  ProgramsQueryParams,
} from 'hooks/programs/programsQuery';
import { PROGRAMS_CACHE_TIME, PROGRAMS_STALE_TIME } from 'hooks/programs/programsQueryCache';

interface PrefetchProgramsParams {
  companies: Company[];
  periodInMainFilter: DataPeriod | null;
  periods?: DataPeriod[] | null;
  isSlotsAviable: boolean;
}

/**
 * Предзагружает список программ выбранной компании, пока HR смотрит таблицу сотрудников.
 *
 * Карточка пациента (InfoStaffModal) запрашивает программы по ключу
 * ['dataPrograms', <uuid компании пациента>, <uuid периода>, '', '', ''],
 * и берёт период не из пропсов, а из sessionStorage.mainFilterPeriodStaff — читаем
 * оттуда же, иначе ключи разойдутся и предзагрузка окажется лишним запросом.
 *
 * Модалки добавления пациента и редактирования заявки ходят с excludeEndedPeriodPrograms
 * и другим ключом (['dataPrograms', 'attach', ...]). Греем оба, чтобы ни одна кнопка
 * не ждала свой первый запрос.
 *
 * Предзагружаем только когда выбрана ровно одна компания: при нескольких компания
 * пациента заранее неизвестна, и греть кэш пришлось бы по всем сразу — это N запросов
 * по ~250 КБ вместо одного нужного.
 */
export const usePrefetchPrograms = ({
  companies,
  periodInMainFilter,
  periods,
  isSlotsAviable,
}: PrefetchProgramsParams): void => {
  const queryClient = useQueryClient();

  const companyUuid: string = companies.length === 1 ? companies[0].uuid : '';

  useEffect(() => {
    if (!isSlotsAviable || !companyUuid) {
      return;
    }

    const mainFilterPeriodStaff =
      'mainFilterPeriodStaff' in sessionStorage
        ? JSON.parse(sessionStorage.getItem('mainFilterPeriodStaff') || '""')
        : '';

    const periodId: string = mainFilterPeriodStaff?.uuid || '';

    // Пока период не подставился (сервисные периоды ещё грузятся, или компанию
    // только что переключили) ключ отличался бы от того, по которому пойдёт карточка,
    // и предзагрузка вылилась бы в лишние 250 КБ. Дождёмся периода — эффект
    // перезапустится по periodInMainFilter.
    if (!periodId) {
      return;
    }

    const prefetch = (params: ProgramsQueryParams): void => {
      // prefetchInfiniteQuery, а не prefetchQuery: карточка читает данные через
      // useInfiniteQuery и ждёт форму { pages, pageParams }.
      // Свежие данные повторно не запрашиваются — prefetch уважает staleTime.
      queryClient
        .prefetchInfiniteQuery(getProgramsQueryKey(params), ({ pageParam = 1 }) => fetchProgramsPage(params, pageParam), {
          staleTime: PROGRAMS_STALE_TIME,
          cacheTime: PROGRAMS_CACHE_TIME,
        })
        .catch(() => {
          // предзагрузка фоновая: ошибку показывать некому, карточка перезапросит сама
        });
    };

    prefetch(
      buildProgramsQueryParams({
        companyUuid,
        periodId,
        excludeEndedPeriodPrograms: false,
      })
    );

    const attachParams = buildProgramsQueryParams({
      companyUuid,
      periodId,
      periods,
      excludeEndedPeriodPrograms: true,
    });

    if (attachParams.isAttachProgramsQuery) {
      prefetch(attachParams);
    }
  }, [queryClient, companyUuid, periodInMainFilter, periods, isSlotsAviable]);
};
